import React, { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { InfoOutlined } from "@mui/icons-material";
import { Modal } from "@mui/material";
import { SPANISH_ABBREV, getAvailableLanguages } from "@bto/shared";
import { VideoPlay } from "../../assets/iconComponents/VideoPlay";
import { Warning } from "../../assets/iconComponents/Warning";
import { SpanishReportButton } from "../shared/SpanishReportButton/SpanishReportButton";
import {
  TestResultsButtonContainer,
  TestResultsRiskDesktopContainer,
  TestResultsCenterContainer,
  TestResultsHr,
  TestResultsInfoContainer,
  TestResultsLearnMoreContainer,
  TestResultsDesktopContainer,
  TestResultsInnerContainer,
  TestResultsOuterContainer,
  TestResultsInfoDesktopContainer,
  TestResultsInnerDesktopContainer,
  TestResultsFetalSexWarning,
  TestResultsViewButton,
  TestResultsViewAction,
  TestResultsWatchVideoContainer,
} from "./TestResults.styles";
import { PartialResultsBanner } from "./PartialResultsBanner";
import {
  Get22qInfo,
  GetCarrierAneuploidyInfo,
  GetCfcInfo,
  GetFetalSexInfo,
  GetRhDInfo,
} from "../shared/Typography/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { TestResultsId } from "./TestResultsId";
import { TestResultsReceived } from "./TestResultsReceived";
import { TestResultsPhysician } from "./TestResultsPhysician";
import { TestResultsCarrierInfo } from "./TestResultsCarrierInfo";
import { TestResultsCfcInfo } from "./TestResultsCfcInfo";
import { TestResultsLearnMore } from "./TestResultsLearnMore";
import { TestResultsAneuploidyRisk } from "./TestResultsAneuploidyRisk";
import { TestResultsRhdInfo } from "./TestResultsRhdInfo";
import { TestResultsFetalSexInfo } from "./TestResultsFetalSexInfo";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  ANEUPLOIDY_PAGE_URL,
  CARRIER_PAGE_URL,
  CFC_PAGE_URL,
  HELP_PAGE_URL,
} from "../../utils/urlConstants";
import {
  getAllRequisitions,
  getSpecificRequisition,
  getUserAuth,
} from "../../recoil/selectors";
import { ProcessingToolTip } from "../../pages/Landing/Landing.styles";
import { SendActionType } from "../shared/Services/SharedServices";
import { TestResults22q } from "./TestResults22QInfo";
import BrowseVideoButton from "../../pages/Video/BrowseVideoButton";
import { FetalSexDownloadWarningModal } from "../DetailedResults/FetalSexDownloadWarningModal";
import { FetalSexModalState } from "../DetailedResults/DetailedResults.types";
import {
  useReportDownload,
  ReportType,
  shouldShowAneuploidyFetalSexWarning,
  shouldShowCarrierFetalSexWarning,
} from "../../utils/hooks/useReportDownload";
import { useResultsTrackerConfig } from "../../utils/hooks/useResultsTrackerConfig";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import {
  STANDALONE_FETAL_SEX_EXPERIENCE,
  type StandaloneFetalSexExperience,
} from "../../utils/hooks/useStandaloneFetalSexRollout";
import { OrderStatusTracker } from "./OrderStatusTracker/OrderStatusTracker";
import { DeriveTrackerInput } from "./OrderStatusTracker/trackerSteps";
import {
  STANDALONE_FETAL_SEX,
  type RequisitionModelSerializer,
  type ReportStatus,
} from "../../recoil/types";

const CFC_PROCESSING_STATUS: ReportStatus = "Processing";
const CFC_STATUSES_WITHOUT_RESULTS = new Set<ReportStatus>([
  CFC_PROCESSING_STATUS,
  "Not Ordered",
  "Ordered",
]);

const getCfcCardState = (
  requisition: RequisitionModelSerializer | undefined,
) => {
  const cfcReport = requisition?.cfc_reports?.[0];
  const displayStatus =
    cfcReport?.status ??
    (requisition?.is_cfc_included ? CFC_PROCESSING_STATUS : undefined);
  const shouldRender = Boolean(
    requisition?.is_cfc_included || cfcReport?.is_ordered,
  );
  const resultsAvailable = Boolean(
    displayStatus && !CFC_STATUSES_WITHOUT_RESULTS.has(displayStatus),
  );
  return {
    display: GetCfcInfo(displayStatus),
    resultsAvailable,
    shouldRender,
    status: displayStatus,
  };
};

type ResultDisplay = ReturnType<typeof GetCarrierAneuploidyInfo>;

const translateResultDisplay = (
  displayWithIcon: ResultDisplay,
  t: TFunction<"translation">,
): ResultDisplay => {
  const displayKeyByValue = {
    Detected: "results.resultStatus.detected",
    "Low Risk": "results.resultStatus.lowRisk",
    Negative: "results.resultStatus.negative",
    "Not Detected": "results.resultStatus.notDetected",
    "Not Ordered": "results.resultStatus.notOrdered",
    "Opted out": "results.resultStatus.optedOutLower",
    "Opted Out": "results.resultStatus.optedOut",
    Ordered: "results.resultStatus.ordered",
    Processing: "results.resultStatus.processing",
    "See Report": "results.resultStatus.seeReport",
    "Test Not Performed": "results.resultStatus.testNotPerformed",
  } as const;
  const displayKey =
    displayKeyByValue[
      displayWithIcon.display as keyof typeof displayKeyByValue
    ];

  return {
    ...displayWithIcon,
    rawDisplay: displayWithIcon.rawDisplay || displayWithIcon.display,
    display: displayKey ? t(displayKey) : displayWithIcon.display,
  };
};

export const TestResults: React.FC<{
  requisitionID: string;
  standaloneFetalSexExperience: StandaloneFetalSexExperience;
}> = ({ requisitionID, standaloneFetalSexExperience }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const auth = useRecoilValue(getUserAuth);
  const requisition = useRecoilValue(getSpecificRequisition(requisitionID));
  const { isDesktop, deviceType } = useDeviceHook();
  const carrierStatus =
    requisition?.unity_reports && requisition?.unity_reports[0]?.status;
  const carrierStatusValid =
    carrierStatus &&
    carrierStatus !== "Processing" &&
    carrierStatus !== "Not Ordered";
  const aneuploidyStatusLowRisk =
    requisition?.aneuploidy_reports &&
    requisition?.aneuploidy_reports[0]?.summary === "Low Risk";
  const aneuploidyStatus =
    requisition?.aneuploidy_reports &&
    requisition?.aneuploidy_reports[0]?.status;
  const aneuploidyStatusValid =
    aneuploidyStatus &&
    aneuploidyStatus !== "Processing" &&
    aneuploidyStatus !== "Not Ordered";
  const aneuploidyReport =
    requisition?.aneuploidy_reports && requisition?.aneuploidy_reports[0];
  const carrierReport =
    requisition?.unity_reports && requisition?.unity_reports[0];

  const isStandaloneFetalSexEnabled =
    standaloneFetalSexExperience === STANDALONE_FETAL_SEX_EXPERIENCE.STANDALONE;
  const shouldShowLegacyFetalSex =
    standaloneFetalSexExperience === STANDALONE_FETAL_SEX_EXPERIENCE.LEGACY;
  const isStandaloneFetalSexResolved =
    standaloneFetalSexExperience !== STANDALONE_FETAL_SEX_EXPERIENCE.LOADING;

  const aneuploidyFetalSexWarning = shouldShowAneuploidyFetalSexWarning(
    aneuploidyReport?.report_data?.fetal_sex,
    requisition?.standalone_fetal_sex_result?.standalone_fetal_sex,
    isStandaloneFetalSexEnabled,
  );
  const showHiddenCorrectionFetalSexWarning =
    isStandaloneFetalSexEnabled &&
    requisition?.standalone_fetal_sex_result?.standalone_fetal_sex ===
      STANDALONE_FETAL_SEX.HIDDEN;
  const carrierFetalSexWarning = shouldShowCarrierFetalSexWarning(
    carrierReport?.report_data?.fetal_sex,
  );

  useEffect(() => {
    const sendUIAction = async () => {
      try {
        await SendActionType(
          "ViewDashboard",
          requisitionID,
          auth?.access_token,
        );
      } catch (e) {
        return undefined;
      }
    };
    sendUIAction();
  }, []);

  const carrierButton = (
    <TestResultsViewButton
      deviceType={deviceType}
      onClick={() => {
        history.push(`${CARRIER_PAGE_URL}/${requisitionID}`);
      }}
    >
      {t("results.actions.viewResults")}
    </TestResultsViewButton>
  );

  const aneuploidyButton = (
    <TestResultsViewAction>
      <TestResultsViewButton
        deviceType={deviceType}
        onClick={() => {
          history.push(`${ANEUPLOIDY_PAGE_URL}/${requisitionID}`);
        }}
      >
        {t("results.actions.viewResults")}
      </TestResultsViewButton>
      {showHiddenCorrectionFetalSexWarning && (
        <TestResultsFetalSexWarning data-testid="aneuploidy-fetal-sex-warning">
          <Warning />
          {GetTypographyContent({
            content: t("results.detail.resultsContainSex"),
            size: "small",
            textColor: "redDark",
          })}
        </TestResultsFetalSexWarning>
      )}
    </TestResultsViewAction>
  );

  const processingToolTip = (
    <ProcessingToolTip title={t("results.dashboard.processingTooltip")}>
      <InfoOutlined />
    </ProcessingToolTip>
  );

  const {
    display: cfcDisplayRaw,
    resultsAvailable: cfcResultsAvailable,
    shouldRender: shouldRenderCfcSection,
    status: cfcStatus,
  } = getCfcCardState(requisition);
  const cfcDisplay = translateResultDisplay(cfcDisplayRaw, t);
  const getTranslatedCarrierAneuploidyInfo = (
    report: Parameters<typeof GetCarrierAneuploidyInfo>[0],
  ) => translateResultDisplay(GetCarrierAneuploidyInfo(report), t);
  const getTranslatedRhDInfo = (rhdValue: Parameters<typeof GetRhDInfo>[0]) =>
    translateResultDisplay(GetRhDInfo(rhdValue), t);
  const getTranslated22qInfo = (del22q: Parameters<typeof Get22qInfo>[0]) =>
    translateResultDisplay(Get22qInfo(del22q), t);
  const getTranslatedFetalSexInfo = (
    fetalSexInfo: Parameters<typeof GetFetalSexInfo>[0],
    aneuploidyProcessing?: boolean,
  ) =>
    translateResultDisplay(
      GetFetalSexInfo(fetalSexInfo, aneuploidyProcessing),
      t,
    );
  const { enabled: isCfcEnabled } = useBooleanFeatureFlag(FeatureFlags.Cfc);
  const { enabled: isPartialResultsBannerEnabled } = useBooleanFeatureFlag(
    FeatureFlags.PartialResultsBanner,
  );
  const { enabled: isCarrierResultVideosEnabled } = useBooleanFeatureFlag(
    FeatureFlags.CarrierResultVideos,
  );

  const cfcResultsUrl = `${CFC_PAGE_URL}/${requisitionID}`;

  const cfcButton = (
    <TestResultsViewButton
      aria-label={t("results.dashboard.viewUnityConfirmResults")}
      deviceType={deviceType}
      onClick={() => {
        history.push(cfcResultsUrl);
      }}
    >
      {t("results.actions.viewResults")}
    </TestResultsViewButton>
  );

  const cfcProcessingToolTip = cfcStatus === "Processing" && processingToolTip;

  const renderCfcSection = () => {
    if (!isCfcEnabled || !shouldRenderCfcSection) return null;

    const cfcAction = (cfcResultsAvailable || cfcProcessingToolTip) && (
      <>
        {cfcResultsAvailable && cfcButton}
        {cfcProcessingToolTip}
      </>
    );

    return (
      <>
        <TestResultsHr />
        {TestResultsCfcInfo(
          cfcDisplay,
          cfcAction,
          cfcResultsAvailable,
          t("results.resultStatus.seeReport"),
        )}
      </>
    );
  };

  const hasCarrierReportVideos = Boolean(
    isCarrierResultVideosEnabled &&
    carrierReport?.report_data?.carrier_report_videos?.length,
  );

  // Report download hook and local state
  const { downloadReport } = useReportDownload();
  const [downloadingSpanish, setDownloadingSpanish] =
    useState<ReportType | null>(null);
  const [showFetalSexModal, setShowFetalSexModal] =
    useState<FetalSexModalState>("");
  const [currentReportType, setCurrentReportType] =
    useState<ReportType>("aneuploidy");

  const getAvailableLanguagesForReport = (reportType: ReportType): string[] => {
    const report =
      reportType === "aneuploidy" ? aneuploidyReport : carrierReport;
    return getAvailableLanguages(report?.pdf_urls_by_language);
  };

  const handleSpanishDownload = async (
    reportType: ReportType,
    hasFetalSexWarning: boolean,
  ) => {
    const reportId =
      reportType === "aneuploidy" ? aneuploidyReport?.id : carrierReport?.id;
    if (hasFetalSexWarning) {
      setCurrentReportType(reportType);
      setShowFetalSexModal("report_es");
    } else if (!downloadingSpanish) {
      setDownloadingSpanish(reportType);
      try {
        await downloadReport(
          reportId,
          reportType,
          SPANISH_ABBREV,
          getAvailableLanguagesForReport(reportType),
          "dashboard",
        );
      } catch (error) {
        console.error("Failed to download Spanish report:", error);
      } finally {
        setDownloadingSpanish(null);
      }
    }
  };

  // Spanish report buttons
  const renderSpanishButton = (reportType: ReportType) => {
    const report =
      reportType === "aneuploidy" ? aneuploidyReport : carrierReport;
    const hasFetalSexWarning =
      reportType === "aneuploidy"
        ? aneuploidyFetalSexWarning
        : carrierFetalSexWarning;

    if (!report?.pdf_urls_by_language?.[SPANISH_ABBREV]) {
      return null;
    }

    return (
      <SpanishReportButton
        onClick={() =>
          handleSpanishDownload(reportType, Boolean(hasFetalSexWarning))
        }
        isDownloading={downloadingSpanish === reportType}
        testId={`view-spanish-${reportType}-report`}
      />
    );
  };

  const aneuploidySpanishButton = renderSpanishButton("aneuploidy");
  const carrierSpanishButton = renderSpanishButton("carrier");

  const requistions = useRecoilValue(getAllRequisitions);
  let isRedrawParent = false;
  const isCarrierOrdered = carrierReport?.is_ordered;
  const isAneuOrdered = aneuploidyReport?.is_ordered;

  const bannerCoversCarrier =
    isPartialResultsBannerEnabled && isAneuOrdered && aneuploidyStatusValid;
  const carrierProccessingToolTip =
    carrierStatus === "Processing" && !bannerCoversCarrier && processingToolTip;

  const bannerCoversAneuploidy =
    isPartialResultsBannerEnabled && isCarrierOrdered && carrierStatusValid;
  const aneuploidyProccessingToolTip =
    aneuploidyStatus === "Processing" &&
    !bannerCoversAneuploidy &&
    processingToolTip;

  // Results tracker experiment (SWE-5075): while a test is still in progress,
  // the tracker replaces that test's status row. Once results are ready the
  // existing card renders unchanged.
  const tracker = useResultsTrackerConfig();
  // Tracker shows until results are truly ready (Final/Corrected). Note: a
  // status of "Ordered" is NOT ready - it is the Kit received tracker step.
  const isAneuReady =
    aneuploidyStatus === "Final" || aneuploidyStatus === "Corrected";
  const isCarrierReady =
    carrierStatus === "Final" || carrierStatus === "Corrected";
  const showAneuploidyTracker =
    tracker.enabled && Boolean(isAneuOrdered) && !isAneuReady;
  const showCarrierTracker =
    tracker.enabled && Boolean(isCarrierOrdered) && !isCarrierReady;

  const aneuploidyTrackerInput: DeriveTrackerInput = {
    status: aneuploidyStatus || "Processing",
    providerReleasedTime: aneuploidyReport?.provider_released_time ?? null,
    dateReceived: requisition?.date_received ?? "",
    estimatedDate: requisition?.estimated_aneuploidy_report_date ?? null,
  };
  const carrierTrackerInput: DeriveTrackerInput = {
    status: carrierStatus || "Processing",
    providerReleasedTime: carrierReport?.provider_released_time ?? null,
    dateReceived: requisition?.date_received ?? "",
    estimatedDate: requisition?.estimated_unity_report_date ?? null,
  };

  const aneuploidyTracker = (
    <OrderStatusTracker
      input={aneuploidyTrackerInput}
      mode={tracker.mode}
      showEstimatedDate={tracker.showEstimatedDate}
      variant={tracker.variant}
      testName="aneuploidy"
      title="Aneuploidy Screen"
    />
  );
  const carrierTracker = (
    <OrderStatusTracker
      input={carrierTrackerInput}
      mode={tracker.mode}
      showEstimatedDate={tracker.showEstimatedDate}
      variant={tracker.variant}
      testName="carrier"
      title="Carrier Screen"
    />
  );

  if (requistions && requisition) {
    isRedrawParent = requistions.some(
      (req) => req.redraw_parent === requisition.req_identifier,
    );
  }
  return (
    <>
      {requisition && (
        <TestResultsCenterContainer isRedrawParent={isRedrawParent}>
          {!isDesktop && (
            <>
              <TestResultsOuterContainer>
                <TestResultsInnerContainer deviceType={deviceType}>
                  {TestResultsId(requisition, t("results.dashboard.testId"))}
                  {TestResultsReceived(
                    requisition.date_received,
                    t("results.dashboard.received"),
                  )}
                  {TestResultsPhysician(
                    requisition.physician_name,
                    t("results.dashboard.physician"),
                  )}
                </TestResultsInnerContainer>
              </TestResultsOuterContainer>

              {isAneuOrdered && isCarrierOrdered && <TestResultsHr />}
              {isAneuOrdered && showAneuploidyTracker && aneuploidyTracker}
              {isAneuOrdered && !showAneuploidyTracker && (
                <>
                  <TestResultsInfoContainer>
                    {TestResultsAneuploidyRisk(
                      getTranslatedCarrierAneuploidyInfo(
                        requisition.aneuploidy_reports &&
                          requisition.aneuploidy_reports[0],
                      ),
                      t("results.testNames.aneuploidyScreen"),
                    )}
                  </TestResultsInfoContainer>
                  <TestResultsLearnMoreContainer deviceType={deviceType}>
                    {TestResultsLearnMore(
                      <TestResultsWatchVideoContainer data-testid="aneuploidy-video-link">
                        {aneuploidyStatusValid && aneuploidyStatusLowRisk ? (
                          <>
                            {t("results.actions.watchVideoResults")}{" "}
                            <VideoPlay />
                          </>
                        ) : (
                          t("results.actions.learnMore")
                        )}
                      </TestResultsWatchVideoContainer>,
                      aneuploidyStatusValid && aneuploidyStatusLowRisk
                        ? `${ANEUPLOIDY_PAGE_URL}/${requisitionID}#aneuploidyInfoVideo`
                        : `${HELP_PAGE_URL}#aneuploidyTestFor-help`,
                      "10px 20px 10px 25px",
                    )}
                  </TestResultsLearnMoreContainer>

                  {aneuploidyReport?.report_data.fetal_rhd_fa !==
                    "Not Ordered" &&
                    aneuploidyReport?.report_data.fetal_rhd_fa !==
                      "Opted out" && (
                      <TestResultsInfoContainer>
                        {TestResultsRhdInfo(
                          getTranslatedRhDInfo(
                            aneuploidyReport?.report_data.fetal_rhd_fa,
                          ),
                          t("results.testNames.rhdFetalAntigen"),
                        )}
                      </TestResultsInfoContainer>
                    )}
                  {aneuploidyReport?.report_data.del22q &&
                    aneuploidyReport?.report_data.del22q !== "Opted out" && (
                      <TestResultsInfoContainer>
                        {TestResults22q(
                          getTranslated22qInfo(
                            aneuploidyReport?.report_data.del22q,
                          ),
                        )}
                      </TestResultsInfoContainer>
                    )}
                  <TestResultsLearnMoreContainer deviceType={deviceType}>
                    {aneuploidyStatusValid &&
                      aneuploidyReport?.report_data.del22q === "Low Risk" &&
                      TestResultsLearnMore(
                        <TestResultsWatchVideoContainer>
                          <>
                            {t("results.actions.watchVideoResults")}{" "}
                            <VideoPlay />
                          </>
                        </TestResultsWatchVideoContainer>,

                        `${ANEUPLOIDY_PAGE_URL}/${requisitionID}#twentyTwoQInfoVideo`,
                        "10px 20px 10px 25px",
                      )}
                  </TestResultsLearnMoreContainer>
                  {/* TODO(SFS_FLAG_CLEANUP): Remove the legacy fetal-sex path after rollout. */}
                  {shouldShowLegacyFetalSex && (
                    <TestResultsInfoContainer data-testid="legacy-fetal-sex-row">
                      {TestResultsFetalSexInfo(
                        getTranslatedFetalSexInfo(
                          aneuploidyReport?.report_data.fetal_sex,
                          aneuploidyStatus === "Processing",
                        ),
                        requisitionID,
                        Boolean(aneuploidyStatusValid),
                        t("results.detail.fetalSex"),
                      )}
                    </TestResultsInfoContainer>
                  )}
                  {isPartialResultsBannerEnabled && (
                    <PartialResultsBanner
                      processingStatus={aneuploidyStatus}
                      otherStatusValid={
                        isCarrierOrdered ? carrierStatusValid : undefined
                      }
                      estimatedDate={
                        requisition.estimated_aneuploidy_report_date
                      }
                      testName={t("results.testNames.aneuploidyScreening")}
                      processingTest="aneuploidy"
                      requisitionID={requisitionID}
                    />
                  )}
                  <TestResultsButtonContainer
                    deviceType={deviceType}
                    processing={aneuploidyStatus === "Processing"}
                  >
                    {aneuploidyStatusValid && aneuploidyButton}
                    {aneuploidyStatusValid && aneuploidySpanishButton}
                    {aneuploidyProccessingToolTip}
                  </TestResultsButtonContainer>
                </>
              )}

              {isAneuOrdered && isCarrierOrdered && <TestResultsHr />}
              {isCarrierOrdered && showCarrierTracker && carrierTracker}
              {isCarrierOrdered && !showCarrierTracker && (
                <>
                  <TestResultsInfoContainer>
                    {TestResultsCarrierInfo(
                      getTranslatedCarrierAneuploidyInfo(
                        requisition.unity_reports &&
                          requisition.unity_reports[0],
                      ),
                      t("results.testNames.carrierScreen"),
                    )}
                  </TestResultsInfoContainer>
                  <TestResultsLearnMoreContainer deviceType={deviceType}>
                    {TestResultsLearnMore(
                      <TestResultsWatchVideoContainer data-testid="carrier-video-link">
                        {carrierStatusValid && hasCarrierReportVideos ? (
                          <>
                            {t("results.actions.watchVideoResults")}{" "}
                            <VideoPlay />
                          </>
                        ) : (
                          t("results.actions.learnMore")
                        )}
                      </TestResultsWatchVideoContainer>,
                      carrierStatusValid && hasCarrierReportVideos
                        ? `${CARRIER_PAGE_URL}/${requisitionID}#carrierInfoVideo`
                        : `${HELP_PAGE_URL}#carrierTestFor-help`,
                      "10px 20px 10px 25px",
                    )}
                  </TestResultsLearnMoreContainer>

                  {isPartialResultsBannerEnabled && (
                    <PartialResultsBanner
                      processingStatus={carrierStatus}
                      otherStatusValid={
                        isAneuOrdered ? aneuploidyStatusValid : undefined
                      }
                      estimatedDate={requisition.estimated_unity_report_date}
                      testName={t("results.testNames.carrierScreen")}
                      processingTest="carrier"
                      requisitionID={requisitionID}
                    />
                  )}
                  <TestResultsButtonContainer
                    deviceType={deviceType}
                    processing={carrierStatus === "Processing"}
                  >
                    {carrierStatusValid && carrierButton}
                    {carrierStatusValid && carrierSpanishButton}
                    {carrierProccessingToolTip}
                  </TestResultsButtonContainer>
                </>
              )}

              {renderCfcSection()}
              <>
                <TestResultsHr />
                <TestResultsLearnMoreContainer deviceType={deviceType}>
                  <div style={{ marginLeft: "20px" }}>
                    <BrowseVideoButton />
                  </div>
                </TestResultsLearnMoreContainer>
              </>
            </>
          )}
          {isDesktop && (
            <>
              <TestResultsDesktopContainer>
                <TestResultsInnerDesktopContainer>
                  {TestResultsId(requisition, t("results.dashboard.testId"))}
                </TestResultsInnerDesktopContainer>
                <TestResultsInnerDesktopContainer>
                  {TestResultsReceived(
                    requisition.date_received,
                    t("results.dashboard.received"),
                  )}
                </TestResultsInnerDesktopContainer>
                <TestResultsInnerDesktopContainer>
                  {TestResultsPhysician(
                    requisition.physician_name,
                    t("results.dashboard.physician"),
                  )}
                </TestResultsInnerDesktopContainer>
              </TestResultsDesktopContainer>
              {isAneuOrdered && isCarrierOrdered && <TestResultsHr />}
              {isAneuOrdered && showAneuploidyTracker && aneuploidyTracker}
              {isAneuOrdered && !showAneuploidyTracker && (
                <>
                  <TestResultsRiskDesktopContainer>
                    {TestResultsAneuploidyRisk(
                      getTranslatedCarrierAneuploidyInfo(aneuploidyReport),
                      t("results.testNames.aneuploidyScreen"),
                    )}
                    <TestResultsButtonContainer
                      deviceType={deviceType}
                      processing={aneuploidyStatus === "Processing"}
                    >
                      {aneuploidyStatusValid && aneuploidyButton}
                      {aneuploidyStatusValid && aneuploidySpanishButton}
                      {aneuploidyProccessingToolTip}
                    </TestResultsButtonContainer>
                  </TestResultsRiskDesktopContainer>
                  <TestResultsLearnMoreContainer deviceType={deviceType}>
                    {TestResultsLearnMore(
                      <TestResultsWatchVideoContainer data-testid="aneuploidy-video-link">
                        {aneuploidyStatusValid && aneuploidyStatusLowRisk ? (
                          <>
                            {t("results.actions.watchVideoResults")}{" "}
                            <VideoPlay />
                          </>
                        ) : (
                          t("results.actions.learnMore")
                        )}
                      </TestResultsWatchVideoContainer>,
                      aneuploidyStatusValid && aneuploidyStatusLowRisk
                        ? `${ANEUPLOIDY_PAGE_URL}/${requisitionID}#aneuploidyInfoVideo`
                        : `${HELP_PAGE_URL}#aneuploidyTestFor-help`,
                      "10px 20px 10px 25px",
                    )}
                  </TestResultsLearnMoreContainer>

                  {aneuploidyReport?.report_data.fetal_rhd_fa !==
                    "Not Ordered" &&
                    aneuploidyReport?.report_data.fetal_rhd_fa !==
                      "Opted out" && (
                      <TestResultsInfoDesktopContainer>
                        {TestResultsRhdInfo(
                          getTranslatedRhDInfo(
                            aneuploidyReport?.report_data.fetal_rhd_fa,
                          ),
                          t("results.testNames.rhdFetalAntigen"),
                        )}
                      </TestResultsInfoDesktopContainer>
                    )}
                  {aneuploidyReport?.report_data.del22q &&
                    aneuploidyReport?.report_data.del22q !== "Opted out" && (
                      <TestResultsInfoDesktopContainer>
                        {TestResults22q(
                          getTranslated22qInfo(
                            aneuploidyReport?.report_data.del22q,
                          ),
                        )}
                      </TestResultsInfoDesktopContainer>
                    )}
                  <TestResultsLearnMoreContainer deviceType={deviceType}>
                    {aneuploidyReport?.report_data.del22q === "Low Risk" &&
                      aneuploidyStatusValid &&
                      TestResultsLearnMore(
                        <TestResultsWatchVideoContainer>
                          <>
                            {t("results.actions.watchVideoResults")}{" "}
                            <VideoPlay />
                          </>
                        </TestResultsWatchVideoContainer>,

                        `${ANEUPLOIDY_PAGE_URL}/${requisitionID}#twentyTwoQInfoVideo`,
                        "10px 20px 10px 25px",
                      )}
                  </TestResultsLearnMoreContainer>
                  {/* TODO(SFS_FLAG_CLEANUP): Remove the legacy fetal-sex path after rollout. */}
                  {shouldShowLegacyFetalSex && (
                    <TestResultsInfoDesktopContainer data-testid="legacy-fetal-sex-row">
                      {TestResultsFetalSexInfo(
                        getTranslatedFetalSexInfo(
                          aneuploidyReport?.report_data.fetal_sex,
                          aneuploidyStatus === "Processing",
                        ),
                        requisitionID,
                        Boolean(aneuploidyStatusValid),
                        t("results.detail.fetalSex"),
                      )}
                    </TestResultsInfoDesktopContainer>
                  )}
                  {isPartialResultsBannerEnabled && (
                    <PartialResultsBanner
                      processingStatus={aneuploidyStatus}
                      otherStatusValid={
                        isCarrierOrdered ? carrierStatusValid : undefined
                      }
                      estimatedDate={
                        requisition.estimated_aneuploidy_report_date
                      }
                      testName={t("results.testNames.aneuploidyScreening")}
                      processingTest="aneuploidy"
                      requisitionID={requisitionID}
                    />
                  )}
                </>
              )}

              {isAneuOrdered && isCarrierOrdered && <TestResultsHr />}
              {isCarrierOrdered && showCarrierTracker && carrierTracker}
              {isCarrierOrdered && !showCarrierTracker && (
                <>
                  <TestResultsRiskDesktopContainer>
                    {TestResultsCarrierInfo(
                      getTranslatedCarrierAneuploidyInfo(carrierReport),
                      t("results.testNames.carrierScreen"),
                    )}
                    <TestResultsButtonContainer
                      deviceType={deviceType}
                      processing={carrierStatus === "Processing"}
                    >
                      {carrierStatusValid && carrierButton}
                      {carrierStatusValid && carrierSpanishButton}
                      {carrierProccessingToolTip}
                    </TestResultsButtonContainer>
                  </TestResultsRiskDesktopContainer>
                  <TestResultsLearnMoreContainer deviceType={deviceType}>
                    {TestResultsLearnMore(
                      <TestResultsWatchVideoContainer data-testid="carrier-video-link">
                        {carrierStatusValid && hasCarrierReportVideos ? (
                          <>
                            {t("results.actions.watchVideoResults")}{" "}
                            <VideoPlay />
                          </>
                        ) : (
                          t("results.actions.learnMore")
                        )}
                      </TestResultsWatchVideoContainer>,
                      carrierStatusValid && hasCarrierReportVideos
                        ? `${CARRIER_PAGE_URL}/${requisitionID}#carrierInfoVideo`
                        : `${HELP_PAGE_URL}#carrierTestFor-help`,
                      "10px 20px 10px 25px",
                    )}
                  </TestResultsLearnMoreContainer>
                  {isPartialResultsBannerEnabled && (
                    <PartialResultsBanner
                      processingStatus={carrierStatus}
                      otherStatusValid={
                        isAneuOrdered ? aneuploidyStatusValid : undefined
                      }
                      estimatedDate={requisition.estimated_unity_report_date}
                      testName={t("results.testNames.carrierScreen")}
                      processingTest="carrier"
                      requisitionID={requisitionID}
                    />
                  )}
                </>
              )}

              {renderCfcSection()}
              <>
                <TestResultsHr />
                <TestResultsRiskDesktopContainer>
                  <BrowseVideoButton />
                </TestResultsRiskDesktopContainer>
              </>
            </>
          )}
        </TestResultsCenterContainer>
      )}

      {/* Fetal Sex Confirmation Modal for Spanish Reports */}
      {(isStandaloneFetalSexResolved || showFetalSexModal) && (
        <Modal
          open={Boolean(showFetalSexModal)}
          onClose={() => setShowFetalSexModal("")}
          style={{
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
          }}
        >
          <FetalSexDownloadWarningModal
            analyticsPageName="LandingPage"
            showModal={setShowFetalSexModal}
            onConfirmDownload={() => {
              const reportId =
                currentReportType === "aneuploidy"
                  ? aneuploidyReport?.id
                  : carrierReport?.id;
              return downloadReport(
                reportId,
                currentReportType,
                showFetalSexModal === "report_es" ? SPANISH_ABBREV : undefined,
                getAvailableLanguagesForReport(currentReportType),
                "dashboard",
              );
            }}
          />
        </Modal>
      )}
    </>
  );
};
